import { useCallback, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import {
  DialogSelectTriggerContent,
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuSearch,
  PrimaryNavigationMenuShowMoreItem,
} from 'components/ui';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import {
  FirmDocumentDialogSearchableSelectRoot,
  FirmDocumentDialogSearchableSelectTrigger,
} from './FirmDocumentDialogSearchableSelect.styles';
import type {
  FirmDocumentDialogSearchableSelectOption,
  FirmDocumentDialogSearchableSelectProps,
} from './FirmDocumentDialogSearchableSelect.types';

const DEFAULT_VISIBLE_LIMIT = 10;
const DEFAULT_MENU_MAX_WIDTH = 320;
const DEFAULT_MENU_MIN_WIDTH = 240;

const FirmDocumentDialogSearchableSelect = ({
  clearSearchAriaLabel,
  disabled = false,
  emptyLabel,
  isLoading = false,
  itemLabelPlural,
  itemLabelSingular,
  label,
  menuTestId,
  onChange,
  options,
  placeholder,
  renderOptionLabel,
  searchPlaceholder,
  searchTestId,
  selectTestId,
  selectedId,
  testIdForOption,
  visibleLimit = DEFAULT_VISIBLE_LIMIT,
}: FirmDocumentDialogSearchableSelectProps) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const selectedOption = options.find(option => option.id === selectedId);
  const {
    canShowFewerOptions,
    filteredOptions,
    hasMoreOptions,
    hiddenOptionCount,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    visibleOptions,
  } = useSearchableLimitedOptions({
    getSearchText: option => option.searchText ?? option.label,
    options,
    visibleLimit,
  });
  const isMenuOpen = Boolean(anchorEl);
  const isSelectDisabled = disabled || isLoading;
  const triggerWidth = anchorEl?.getBoundingClientRect().width ?? 0;
  const menuMinWidth = Math.max(DEFAULT_MENU_MIN_WIDTH, Math.ceil(triggerWidth));
  const menuMaxWidth = Math.max(DEFAULT_MENU_MAX_WIDTH, menuMinWidth);
  const getOptionLabel = (option: FirmDocumentDialogSearchableSelectOption) => (
    renderOptionLabel?.(option) ?? option.label
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    resetSearchableOptions();
  }, [resetSearchableOptions]);

  const handleSelectClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!isSelectDisabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSelectOption = (id: number | null) => {
    onChange(id);
    handleMenuClose();
  };

  return (
    <FirmDocumentDialogSearchableSelectRoot>
      {label}
      <FirmDocumentDialogSearchableSelectTrigger
        aria-expanded={isMenuOpen || undefined}
        aria-haspopup="listbox"
        data-testid={selectTestId}
        disabled={isSelectDisabled}
        hasValue={Boolean(selectedOption)}
        onClick={handleSelectClick}>
        <DialogSelectTriggerContent
          isLoading={isLoading}
          placeholder={placeholder}
          value={selectedOption ? getOptionLabel(selectedOption) : undefined}
        />
      </FirmDocumentDialogSearchableSelectTrigger>
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        data-testid={menuTestId}
        disableAutoFocusItem
        menuMaxWidth={menuMaxWidth}
        menuMinWidth={menuMinWidth}
        onClose={handleMenuClose}
        open={isMenuOpen}
        scrollHintItemLabelPlural={itemLabelPlural}
        scrollHintItemLabelSingular={itemLabelSingular}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
        MenuListProps={{
          disableListWrap: true,
        }}>
        <PrimaryNavigationMenuSearch
          clearAriaLabel={clearSearchAriaLabel}
          fullWidth
          inputRef={searchInputRef}
          onChange={event => setSearchValue(event.target.value)}
          onClear={() => setSearchValue('')}
          placeholder={searchPlaceholder}
          value={searchValue}
          inputProps={{
            'data-testid': searchTestId,
          }}
        />
        {visibleOptions.map(option => (
          <PrimaryNavigationMenuItem
            key={option.id ?? 'root'}
            data-testid={testIdForOption?.(option.id)}
            isActive={option.id === selectedId}
            onClick={() => handleSelectOption(option.id)}>
            {getOptionLabel(option)}
          </PrimaryNavigationMenuItem>
        ))}
        {hasMoreOptions && (
          <PrimaryNavigationMenuShowMoreItem
            count={hiddenOptionCount}
            itemLabelPlural={itemLabelPlural}
            itemLabelSingular={itemLabelSingular}
            onClick={() => setShowAllOptions(true)}
            variant="more"
          />
        )}
        {canShowFewerOptions && (
          <PrimaryNavigationMenuShowMoreItem
            itemLabelPlural={itemLabelPlural}
            itemLabelSingular={itemLabelSingular}
            onClick={() => setShowAllOptions(false)}
            variant="fewer"
          />
        )}
        {filteredOptions.length === 0 && (
          <PrimaryNavigationMenuItem disabled tone="muted">
            {emptyLabel}
          </PrimaryNavigationMenuItem>
        )}
      </PrimaryNavigationMenu>
    </FirmDocumentDialogSearchableSelectRoot>
  );
};

export default FirmDocumentDialogSearchableSelect;
